<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useMFA } from '@/composables/useMFA';
import { useWebAuthn } from '@/composables/useWebAuthn';

const props = defineProps({
  challengeToken: { type: String, required: true },
  availableMethods: { type: Array, default: () => ['totp'] },
  email: { type: String, default: '' },
});

const emit = defineEmits(['success', 'cancel', 'error']);

const mfa = useMFA();
const webauthn = useWebAuthn();

const selectedMethod = ref('totp');
const otpCode = ref(['', '', '', '', '', '']);
const isVerifying = ref(false);
const errorMessage = ref('');
const emailSent = ref(false);
const resendCooldown = ref(0);

const otpInputs = ref([]);

const canUsePasskey = computed(() => {
  return props.availableMethods.includes('passkey') && webauthn.isSupported.value;
});

const fullCode = computed(() => otpCode.value.join(''));

// Handle OTP input
const handleOtpInput = (index, event) => {
  const value = event.target.value;

  // Only allow numbers
  if (!/^\d*$/.test(value)) {
    otpCode.value[index] = '';
    return;
  }

  // Handle paste
  if (value.length > 1) {
    const digits = value.split('').filter((d) => /\d/.test(d)).slice(0, 6);
    digits.forEach((digit, i) => {
      if (i < 6) otpCode.value[i] = digit;
    });
    const lastIndex = Math.min(digits.length - 1, 5);
    otpInputs.value[lastIndex]?.focus();
    return;
  }

  otpCode.value[index] = value;

  // Auto-focus next input
  if (value && index < 5) {
    otpInputs.value[index + 1]?.focus();
  }
};

const handleKeydown = (index, event) => {
  // Handle backspace
  if (event.key === 'Backspace' && !otpCode.value[index] && index > 0) {
    otpInputs.value[index - 1]?.focus();
  }
};

// Auto-submit when 6 digits entered
watch(fullCode, (code) => {
  if (code.length === 6) {
    verifyCode();
  }
});

const verifyCode = async () => {
  if (fullCode.value.length !== 6) {
    errorMessage.value = 'Please enter a 6-digit code';
    return;
  }

  isVerifying.value = true;
  errorMessage.value = '';

  try {
    const response = await mfa.verify2FA(props.challengeToken, fullCode.value, selectedMethod.value);
    emit('success', response);
  } catch (err) {
    errorMessage.value = err.message || 'Invalid code. Please try again.';
    otpCode.value = ['', '', '', '', '', ''];
    otpInputs.value[0]?.focus();
  } finally {
    isVerifying.value = false;
  }
};

const sendEmailCode = async () => {
  if (resendCooldown.value > 0) return;

  try {
    await mfa.requestEmailCode(props.challengeToken);
    emailSent.value = true;
    startResendCooldown();
  } catch (err) {
    errorMessage.value = err.message || 'Failed to send email code';
  }
};

const startResendCooldown = () => {
  resendCooldown.value = 60;
  const interval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      clearInterval(interval);
    }
  }, 1000);
};

const authenticateWithPasskey = async () => {
  isVerifying.value = true;
  errorMessage.value = '';

  try {
    // Get passkey options with its own challengeToken
    const { options, challengeToken } = await mfa.getPasskeyLoginOptions(props.email);
    
    // Check if user has passkeys registered
    if (!options || (Array.isArray(options) && options.length === 0) || !options.challenge) {
      throw new Error('No passkeys available. Please use another authentication method.');
    }
    
    const credential = await webauthn.authenticatePasskey(options);
    // Verify with the passkey's challengeToken
    const response = await mfa.verifyPasskeyLogin(credential, challengeToken);
    emit('success', response);
  } catch (err) {
    if (err.name === 'NotAllowedError') {
      errorMessage.value = 'Passkey authentication was cancelled';
    } else {
      errorMessage.value = err.message || 'Passkey authentication failed';
    }
  } finally {
    isVerifying.value = false;
  }
};

const switchMethod = (method) => {
  selectedMethod.value = method;
  errorMessage.value = '';
  otpCode.value = ['', '', '', '', '', ''];

  if (method === 'email' && !emailSent.value) {
    sendEmailCode();
  }
};

onMounted(() => {
  otpInputs.value[0]?.focus();

  // Auto-send email code if it's the only method
  if (props.availableMethods.length === 1 && props.availableMethods[0] === 'email') {
    selectedMethod.value = 'email';
    sendEmailCode();
  }
});
</script>

<template>
  <div class="mfa-challenge">
    <div class="mfa-header">
      <div class="mfa-icon">
        <i class="fas fa-shield-alt"></i>
      </div>
      <h2 class="mfa-title">Two-Factor Authentication</h2>
      <p class="mfa-subtitle">
        <template v-if="selectedMethod === 'totp'">
          Enter the code from your authenticator app
        </template>
        <template v-else-if="selectedMethod === 'email'">
          Enter the code sent to your email
        </template>
        <template v-else-if="selectedMethod === 'passkey'">
          Use your passkey to sign in
        </template>
      </p>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="mfa-error">
      <i class="fas fa-exclamation-circle"></i>
      {{ errorMessage }}
    </div>

    <!-- OTP Input (for TOTP and Email) -->
    <div v-if="selectedMethod !== 'passkey'" class="otp-container">
      <div class="otp-inputs">
        <input
          v-for="(digit, index) in otpCode"
          :key="index"
          :ref="(el) => (otpInputs[index] = el)"
          type="text"
          inputmode="numeric"
          maxlength="6"
          class="otp-input"
          :class="{ 'has-value': digit }"
          :value="digit"
          @input="handleOtpInput(index, $event)"
          @keydown="handleKeydown(index, $event)"
          @paste.prevent="handleOtpInput(0, $event)"
          :disabled="isVerifying"
        />
      </div>

      <button
        class="mfa-btn mfa-btn-primary"
        @click="verifyCode"
        :disabled="fullCode.length !== 6 || isVerifying"
      >
        <span v-if="isVerifying" class="spinner-border spinner-border-sm me-2"></span>
        {{ isVerifying ? 'Verifying...' : 'Verify' }}
      </button>

      <!-- Resend email code -->
      <div v-if="selectedMethod === 'email'" class="resend-container">
        <button
          class="resend-btn"
          @click="sendEmailCode"
          :disabled="resendCooldown > 0"
        >
          {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code' }}
        </button>
      </div>
    </div>

    <!-- Passkey Authentication -->
    <div v-else class="passkey-container">
      <div class="passkey-icon">
        <i class="fas fa-fingerprint"></i>
      </div>
      <button
        class="mfa-btn mfa-btn-primary"
        @click="authenticateWithPasskey"
        :disabled="isVerifying"
      >
        <span v-if="isVerifying" class="spinner-border spinner-border-sm me-2"></span>
        {{ isVerifying ? 'Authenticating...' : 'Use Passkey' }}
      </button>
    </div>

    <!-- Method Switcher -->
    <div v-if="availableMethods.length > 1" class="method-switcher">
      <span class="method-label">Or use another method:</span>
      <div class="method-options">
        <button
          v-if="availableMethods.includes('totp') && selectedMethod !== 'totp'"
          class="method-btn"
          @click="switchMethod('totp')"
        >
          <i class="fas fa-mobile-alt"></i>
          Authenticator App
        </button>
        <button
          v-if="availableMethods.includes('email') && selectedMethod !== 'email'"
          class="method-btn"
          @click="switchMethod('email')"
        >
          <i class="fas fa-envelope"></i>
          Email Code
        </button>
        <button
          v-if="canUsePasskey && selectedMethod !== 'passkey'"
          class="method-btn"
          @click="switchMethod('passkey')"
        >
          <i class="fas fa-fingerprint"></i>
          Passkey
        </button>
      </div>
    </div>

    <!-- Cancel Button -->
    <button class="mfa-btn mfa-btn-text" @click="emit('cancel')">
      Cancel and go back
    </button>
  </div>
</template>

<style scoped>
.mfa-challenge {
  width: 100%;
  max-width: 420px;
  padding: 2.5rem;
  text-align: center;
  background: rgba(20, 20, 30, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.5),
    0 0 100px rgba(0, 212, 255, 0.1);
  animation: challengeAppear 0.5s ease-out;
}

@keyframes challengeAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.mfa-header {
  margin-bottom: 2rem;
}

.mfa-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.25rem;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 0 30px rgba(0, 212, 255, 0.3),
    0 0 60px rgba(124, 58, 237, 0.2);
  animation: iconGlow 3s ease-in-out infinite;
}

@keyframes iconGlow {
  0%, 100% {
    box-shadow: 0 0 30px rgba(0, 212, 255, 0.3), 0 0 60px rgba(124, 58, 237, 0.2);
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 212, 255, 0.5), 0 0 80px rgba(124, 58, 237, 0.3);
  }
}

.mfa-icon i {
  font-size: 30px;
  color: white;
}

.mfa-title {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #f0f0f5 0%, #a0a0b0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.mfa-subtitle {
  font-size: 0.95rem;
  color: #6b6b7b;
  margin: 0;
}

.mfa-error {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-8px); }
  40% { transform: translateX(8px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}

.otp-container {
  margin-bottom: 1.5rem;
}

.otp-inputs {
  display: flex;
  gap: 0.625rem;
  justify-content: center;
  margin-bottom: 1.75rem;
}

.otp-input {
  width: 50px;
  height: 60px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  background: rgba(30, 30, 42, 0.8);
  color: #f0f0f5;
  transition: all 0.3s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #00d4ff;
  background: rgba(30, 30, 42, 1);
  box-shadow: 
    0 0 0 4px rgba(0, 212, 255, 0.15),
    0 0 20px rgba(0, 212, 255, 0.1);
}

.otp-input.has-value {
  border-color: #00d4ff;
  background: rgba(30, 30, 42, 1);
}

.otp-input:disabled {
  opacity: 0.5;
}

.mfa-btn {
  width: 100%;
  padding: 1rem 1.5rem;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.mfa-btn-primary {
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.mfa-btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.mfa-btn-primary:hover:not(:disabled)::before {
  left: 100%;
}

.mfa-btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
}

.mfa-btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mfa-btn-text {
  background: transparent;
  color: #6b6b7b;
  margin-top: 1rem;
  text-transform: none;
  letter-spacing: 0;
}

.mfa-btn-text:hover {
  color: #00d4ff;
}

.resend-container {
  margin-top: 1rem;
}

.resend-btn {
  background: none;
  border: none;
  color: #00d4ff;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: all 0.3s ease;
}

.resend-btn:hover:not(:disabled) {
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
}

.resend-btn:disabled {
  color: #4b4b5b;
  cursor: not-allowed;
}

.passkey-container {
  padding: 2rem 0;
}

.passkey-icon {
  width: 90px;
  height: 90px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #7c3aed 0%, #ec4899 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: passkeyPulse 2s ease-in-out infinite;
  position: relative;
}

.passkey-icon::before,
.passkey-icon::after {
  content: '';
  position: absolute;
  inset: -8px;
  border: 2px solid rgba(124, 58, 237, 0.3);
  border-radius: 50%;
  animation: passkeyRing 2s ease-out infinite;
}

.passkey-icon::after {
  animation-delay: 0.5s;
}

@keyframes passkeyPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes passkeyRing {
  0% { transform: scale(1); opacity: 1; }
  100% { transform: scale(1.4); opacity: 0; }
}

.passkey-icon i {
  font-size: 40px;
  color: white;
}

.method-switcher {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.method-label {
  display: block;
  font-size: 0.85rem;
  color: #6b6b7b;
  margin-bottom: 1rem;
}

.method-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  justify-content: center;
}

.method-btn {
  padding: 0.75rem 1.125rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  background: rgba(30, 30, 42, 0.6);
  color: #a0a0b0;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.method-btn:hover {
  border-color: #00d4ff;
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.05);
}

.method-btn i {
  font-size: 1rem;
}

/* Spinner override for dark theme */
.spinner-border {
  border-color: rgba(255, 255, 255, 0.2);
  border-right-color: white;
}
</style>
